import { ApplicationDependencyService, ApplicationServerService, DatabaseApplicationService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AppDependency, ApplicationToServer, AppToServerPatchAllConditions,
    DatabaseApplication, DBToAppPatchAllConditions, GeneralDependencyType } from '@amzn/awscat-portfolio-assessment-types';

import { APP_SORT_OPTIONS, AppDatabaseMappingToUpdate, AppServerMappingToUpdate, DEPENDENCY_STATUS, STATUS_IN_GROUPING } from './constants';
import { RemoveApplicationDetail } from './RemoveApplicationModal';

export const dependencyTypeMapping: {[key: string]: string} = {
    'applications-servers-v0': 'Shared Server',
    'databases-applications-v0': 'Shared Database',
    'server-communications-v0': 'Server Communication',
    'user-import': 'Application Dependency',
};

/**
 * Returns a Display friendly resource id for grouping table
 * @param  {string} resourceType
 * @param  {string} resourceId
 * @returns string
 */
export const getDisplayResourceId = (resourceType: string, resourceId: string): string => {
    if (!resourceType || resourceType === 'user-import') {
        return '-';
    }
    return resourceId;
};

/**
 * counts the number of active dependencies that are not shared infrastructure
 * @param  {AppDependency[]} dependencies
 * @param  {string} appId
 * @param  {string} resourceTypePrefix
 * @returns number
 */
export const countDependencies = (dependencies: AppDependency[], appId: string, resourceTypePrefix: string): number => {
    return dependencies.reduce((total, dependency) => {
        return total + (
            dependency.srcAppId === appId && dependency.resourceType.startsWith(resourceTypePrefix) &&
            dependency.status === DEPENDENCY_STATUS.ACTIVE && dependency.inScope && !dependency.isSharedInfrastructure ? 1 : 0);
    }, 0);
};

/**
 * Returns sorted list of applications based on sortType
 * @param  {RemoveApplicationDetail[]} applications
 * @param  {APP_SORT_OPTIONS} sortType
 * @returns RemoveApplicationDetail
 */
export const sortApplications = (applications: RemoveApplicationDetail[], sortType: APP_SORT_OPTIONS): RemoveApplicationDetail[] => {
    switch (sortType) {
        case APP_SORT_OPTIONS.TOTAL:
            return applications.sort((b,a) => (b.numServerDependencies + b.numDBDependencies) - (a.numServerDependencies + a.numDBDependencies));
        case APP_SORT_OPTIONS.D_TOTAL:
            return applications.sort((a,b) => (b.numServerDependencies + b.numDBDependencies) - (a.numServerDependencies + a.numDBDependencies));
        case APP_SORT_OPTIONS.SERVER:
            return applications.sort((b,a) => b.numServerDependencies - a.numServerDependencies);
        case APP_SORT_OPTIONS.D_SERVER:
            return applications.sort((a,b) => b.numServerDependencies - a.numServerDependencies);
        case APP_SORT_OPTIONS.DATABASE:
            return applications.sort((b,a) => b.numDBDependencies - a.numDBDependencies);
        case APP_SORT_OPTIONS.D_DATABASE:
            return applications.sort((a,b) => b.numDBDependencies - a.numDBDependencies);
        default:
            return applications;
    }
};

/**
 * Returns matching appServer Mapping
 * @param  {ApplicationToServer[]} appServerMappings
 * @param  {string} appId
 * @param  {string} serverId
 * @returns ApplicationToServer
 */
export const findApplicationServerMapping = (appServerMappings: ApplicationToServer[], appId: string, serverId: string): ApplicationToServer | undefined => {
    return appServerMappings.find(mapping => mapping.applicationId === appId && mapping.serverId === serverId);
};

/**
 * Retruns matching appDatabase Mapping
 * @param  {DatabaseApplication[]} appDatabaseMappings
 * @param  {string} appId
 * @param  {string} databaseId
 */
export const findApplicationDatabaseMapping = (appDatabaseMappings: DatabaseApplication[], appId: string, databaseId: string)
    : DatabaseApplication | undefined => {
    return appDatabaseMappings.find(mapping => mapping.applicationId === appId && mapping.databaseId === databaseId);
};

/**
 * Returns a check if prev and curr status are different, if so dependency will be updated
 * @param  {string} prevStatus
 * @param  {boolean} currStatusChecked
 * @returns boolean
 */
export const statusDiff = (prevStatus: string, currStatusChecked: boolean): boolean => 
    prevStatus !== STATUS_IN_GROUPING.ACTIVE && currStatusChecked || prevStatus === STATUS_IN_GROUPING.ACTIVE && !currStatusChecked;

/**
 * Returns mapping status based on mapping check and stage
 * @param  {boolean} mappingChecked
 * @returns string
 */
export const decideDetailedMappingStatus = (mappingChecked: boolean, stage: 'confirm' | 'review'): string => {
    if (!mappingChecked && stage === 'review') {
        return STATUS_IN_GROUPING.IN_REVIEW;
    } else if (!mappingChecked && stage === 'confirm') {
        return STATUS_IN_GROUPING.INACTIVE;
    }
    else {
        return STATUS_IN_GROUPING.ACTIVE;
    }
};

/**
 * Updates AppServer Dependencies
 * @param  {AppServerMappingToUpdate[]} mappings
 * @param  {string} portfolioId
 * @param  {ApplicationServerService} appServerService
 * @param  {boolean} updateExactDependencies=false
 * @returns void
 */
export const changeAppServerMappingStatus = async (
    mappings: AppServerMappingToUpdate[],
    portfolioId: string,
    appServerService: ApplicationServerService,
    updateExactDependencies = false,
): Promise<void> => {
    if (!mappings || !mappings.length) {
        return;
    }

    const dependenciesToUpdate: AppToServerPatchAllConditions[] = mappings.map(mapping => ({
        portfolioId,
        applicationId: mapping.applicationId,
        serverId: mapping.serverId,
        fields: {
            statusInGrouping: mapping.status,
        },
    }));

    await appServerService.patchAll(portfolioId, dependenciesToUpdate, { updateDependencies: true, updateExactDependencies });

    return;
};

/**
 * Updates AppDatabase Dependencies
 * @param  {AppDatabaseMappingToUpdate[]} mappings
 * @param  {string} portfolioId
 * @param  {DatabaseApplicationService} dbAppService
 * @param  {} updateExactDependencies=false
 * @returns Promise
 */
export const changeAppDatabaseMappingStatus = async (
    mappings: AppDatabaseMappingToUpdate[],
    portfolioId: string,
    dbAppService: DatabaseApplicationService,
    updateExactDependencies = false,
): Promise<void> => {
    if (!mappings || !mappings.length) {
        return;
    }

    const dependenciesToUpdate: DBToAppPatchAllConditions[] = mappings.map(mapping => ({
        portfolioId,
        applicationId: mapping.applicationId,
        databaseId: mapping.databaseId,
        fields: {
            statusInGrouping: mapping.status,
        },
    }));

    await dbAppService.patchAll(portfolioId, dependenciesToUpdate, { updateDependencies: true, updateExactDependencies });

    return;
};

/**
 * Updates Other types of dependencies after grouping action
 * @param  {DEPENDENCY_STATUS} status
 * @param  {string} portfolioId
 * @param  {GeneralDependencyType[]} dependencies
 * @param  {ApplicationDependencyService} appDependencyService
 * @returns Promise
 */
export const changeDependencyStatus = async (
    status: DEPENDENCY_STATUS,
    portfolioId: string,
    dependencies: GeneralDependencyType[],
    appDependencyService: ApplicationDependencyService,
): Promise<void> => {
    if (!dependencies || !dependencies.length) {
        return;
    }

    await appDependencyService.patch({
        portfolioId,
        applicationDependencies: dependencies,
        fields: {
            status,
        },
    });
};
